<script setup>
import {onMounted, ref} from 'vue';
import {isRebrandedTenant, maskPhoneNumber, setButtonContentDefault, setButtonContentLoading} from '@/helper';
import {generate2faCode} from '@/services/AuthService';
import {ErrorMessage, Field, Form} from 'vee-validate';
import {Tooltip} from 'bootstrap';
import {nextTick} from 'vue';
import {useI18n} from 'vue-i18n';
import userService from '@/services/UserService';
import {useRoute} from 'vue-router';
import clientService from '@/services/ClientService';
import {getTenantData} from '@/services/TenantService';
import OnboardingHeader from '@/components/layouts/OnboardingHeader.vue';
import OnboardingFooter from '@/components/layouts/OnboardingFooter.vue';
import AppAnnouncements from '@/components/AppAnnouncements.vue';

const {t, te} = useI18n();

const params = useRoute().query;

const appName = isRebrandedTenant() ? 'B.I.G. Invest' : 'IN Equity';

const steps = ref([
  {type: 'start', title: t('clientSelfRegister.start', {appName})},
  {type: 'client_form', title: 'Registrace'},
]);

const stepsForOverview = ref([
  {order: 1, icon: 'mdi-lock', title: 'Bezpečná registrace do naší aplikace'},
  {order: 2, icon: 'mdi-help', title: 'Série otázek, díky kterým Vás lépe poznáme'},
  {order: 3, icon: 'mdi-calculator', title: 'Pomůžeme Vám splnit Vaše investiční cíle'},
  {order: 4, icon: 'mdi-fountain-pen-tip', title: 'Vše je on-line včetně podpisu', info_text: 'Mějte připravený doklad totožnosti, případně můžete využít ověření přes BankID'},
]);

const stepIndex = ref(0);
const step = ref(steps.value[stepIndex.value]);
const codeSent = ref({SMS: false, EMAIL: false});
const codeSentLoading = ref({SMS: false, EMAIL: false});
const codeSentAgain = ref({SMS: false, EMAIL: false});
const codeRequestInterval = ref({SMS: 0, EMAIL: 0});
const errorMessage = ref('');
const errorMessageSMS = ref('');
const errorMessageEmail = ref('');
const phone = ref(null);
const email = ref(null);
const clientForm = ref(null);
const createdClient = ref(null);
// Token z odpovědi prvního uložení - BE podle něj pozná, že opravu e-mailu/telefonu přes
// client_id posílá ten, kdo klienta založil. Drží se jen v paměti (ne v localStorage).
const registrationToken = ref(null);
const isEmbedded = ref(false);
const showReferralCode = ref(true);
const emailIsUnique = ref(false);
const showVerification = ref(false);
const emailIsChanged = ref(false);
const phoneIsChanged = ref(false);

onMounted(() => {
  if (window.location.href.includes('embedded')) {
    isEmbedded.value = true;
  }

  if (params.referral_code) {
    localStorage.removeItem('TENANT_THEME');
    getTenantData(true, null, params.referral_code)
        .then(() => {
        });
  }

  nextTick(() => {
    document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => {
      new Tooltip(el);
    });
  });
});

async function checkUniqueEmail(email) {
  const response = await userService.checkUniqueEmail(email);

  if (response.is_unique === true) {
    emailIsUnique.value = true;
    return true;
  } else {
    emailIsUnique.value = false;
    errorMessage.value = t('clientSelfRegister.email_not_unique');
    return false;
  }
}

async function processClientForm() {
  setButtonContentLoading('register-submit-button');

  clientForm.value = {
    ...clientForm.value,
    ...step.value.data,
    name: step.value.data.firstname + ' ' + step.value.data.surname
  };

  const clientId = createdClient.value?.id ?? null;

  if (!clientId || emailIsChanged.value) {
    const emailIsChecked = await checkUniqueEmail(step.value.data.email);

    if (!emailIsChecked || emailIsUnique.value === false) {
      setButtonContentDefault('register-submit-button');
      return;
    }
  }

  if (clientId) {
    clientForm.value.client_id = clientId;
    clientForm.value.registration_token = registrationToken.value;
  }

  let response;
  try {
    response = await userService.saveUserAsClient(clientForm.value);
  } catch (error) {
    // 403 = registrační token vypršel nebo není platný - opravu už nelze uložit, registraci je nutné začít znovu
    errorMessage.value = error?.status === 403
        ? t('clientSelfRegister.registration_expired')
        : (error?.message
            // BE posílá v message buď i18n klíč, nebo hotový text
            ? (te(error.message) ? t(error.message) : error.message)
            : t('clientSelfRegister.registration_failed'));
    setButtonContentDefault('register-submit-button');
    return;
  }

  errorMessage.value = '';
  if (response.client) {
    createdClient.value = response.client;
  }
  // Token vrací BE jen při prvním (anonymním) založení, při opravě údajů zůstává původní
  if (response.registration_token) {
    registrationToken.value = response.registration_token;
  }

  emailIsChanged.value = false;
  phoneIsChanged.value = false;
  phone.value = response.client ? response.client.phone : (response.user ? response.user.phone : null);
  email.value = response.client ? response.client.email : (response.user ? response.user.email : null);
  generateCode('EMAIL');
  generateCode('SMS');
  showVerification.value = true;
  setButtonContentDefault('register-submit-button');
}

function completeVerification() {
  setButtonContentLoading('step-submit-button');
  const verificationCode = step.value.data.verification_code_sms ? step.value.data.verification_code_sms : step.value.data.verification_code_email;
  const body = {
    ...step.value.data,
    verification_code: verificationCode,
    email: clientForm.value.email,
    password: clientForm.value.password
  };

  clientService.verifyClientSelfRegistration(body, createdClient.value.id)
      .then((response) => {
        localStorage.setItem('ACCESS_TOKEN', response.access_token);
        localStorage.setItem('ONBOARDING_LAST_STEP', '1');
        window.location.reload();
      })
      .catch(error => {
        if (error?.message === 'verification_code.INVALID_CODE') {
          errorMessage.value = t('login.invalid_auth_code');
        } else if (error?.message === 'verification_code.SMS.INVALID_CODE') {
          errorMessage.value = t('login.invalid_auth_code_sms');
        } else if (error?.message === 'verification_code.EMAIL.INVALID_CODE') {
          errorMessage.value = t('login.invalid_auth_code_email');
        } else if (error?.message?.endsWith('.TOO_MANY_ATTEMPTS')) {
          // BE vrací klíč s kanálem (verification_code.SMS.TOO_MANY_ATTEMPTS / EMAIL), hláška je společná
          errorMessage.value = t('verification_code.TOO_MANY_ATTEMPTS');
        } else if (error.message) {
          errorMessage.value = error.message;
        }
      })
      .finally(() => {
        setButtonContentDefault('step-submit-button');
      });
}

function nextStep() {
  stepIndex.value++;
  step.value = steps.value[stepIndex.value];
  errorMessage.value = '';

  scrollToTop();

  if (step.value.type === 'client_form') {
    /*if (params.referral_code) {*/
    showReferralCode.value = false;
    /*}*/
    step.value.data = {
      email: params.email,
      phone: params.phone ?? '', // '+' char must be replaced by '%2B' in url
      firstname: params.first_name ?? '',
      surname: params.last_name ?? '',
      referral_code: params.referral_code,
      password: '',
      gdpr_agree: true
    };
  }

  if (step.value.type === 'confirmation') {
    step.value.data.phone = phone.value;
    generateCode();
  }
}

function scrollToTop() {
  // scroll to top of the overlay
  const popup = document.getElementById('app-popup-scrollable-element');
  if (popup) {
    popup.scrollTo({top: 0, behavior: 'smooth'});
  }
}

function endOnboarding() {
  window.location.reload();
}

const generateCode = (service) => {
  codeSentLoading.value[service] = true;
  codeSentAgain.value[service] = true;
  errorMessageSMS.value = null;
  errorMessageEmail.value = null;
  const form = {
    verifiable_type: 'CLIENT',
    verifiable_id: createdClient.value.id,
    service: service,
    recipient: service === 'SMS' ? phone.value : email.value,
  };
  setButtonContentLoading('btn-activate');

  generate2faCode(form, 'api/verification-code/generate').then(response => {
    codeRequestInterval.value[service] = 30;
    codeSent.value = true;
    setButtonContentDefault('btn-activate');
    startStopwatch(service);
    codeSentLoading.value[service] = false;
  }, (error) => {
    if (service === 'SMS') {
      errorMessageSMS.value = error.message;
    } else if (service === 'EMAIL') {
      errorMessageEmail.value = error.message;
    }
    setButtonContentDefault('btn-activate');
    codeSentAgain.value[service] = false;
    codeSentLoading.value[service] = false;
  });
};

const startStopwatch = (service) => {
  const interval = setInterval(() => {
    codeRequestInterval.value[service]--;
    if (codeRequestInterval.value[service] <= 0) {
      clearInterval(interval);
      codeSentAgain.value[service] = false;
    }
  }, 1000);
};

function handleContactChange(contactType) {
  if (contactType === 'phone') {
    if (step.data?.phone !== phone.value) {
      showVerification.value = false;
      phoneIsChanged.value = true;
    }
    phoneIsChanged.value = true;
  } else if (contactType === 'email') {
    if (step.data?.email !== email.value) {
      showVerification.value = false;
      emailIsChanged.value = true;
    }
  }
}
</script>

<template>
  <div :class="{'self-register-embedded': isEmbedded}" class="app-popup">
    <OnboardingHeader v-if="!isEmbedded"></OnboardingHeader>
    <div class="circles-container-wrapper">
      <div class="circles-container" style="top: 310px; right: -220px;">
        <div class="circles__img"></div>
      </div>
      <div class="circles-container" style="left: -130px; top: 20px;">
        <div class="circles__img"></div>
      </div>
    </div>
    <div id="app-popup-scrollable-element" class="row h-100 overflow-auto justify-content-center px-0 py-5 px-lg-5 mx-0" style="z-index: 1055">
      <div class="col-md-6 app-popup__container mb-4">
        <div v-if="!isEmbedded" class="app-popup__title">
          {{ step.title }}
        </div>
        <div class="app-popup__content mb-5">
          <div :class="step.type !== 'start' ? 'card' : ''">
            <div class="card-body">
              <template v-if="step.type === 'start'">
                <!-- IMA-519: úvodní krok zmiňuje ověření přes Bank iD -> upozornění na odstávku Bank iD -->
                <AppAnnouncements :onlyBankId="true"/>
                <div class="row mb-3">
                  <div class="col-12">
                    {{ t('clientSelfRegister.start_text') }}
                  </div>
                </div>
                <div class="row mb-5 justify-content-center description-steps">
                  <template v-for="step of stepsForOverview" class="description-steps-step">
                    <div class="col-md-12">
                      <div class="alert alert-light">
                        <span v-if="step.icon" class="description-steps-step--icon mdi-18px me-2 mdi" :class="step.icon" style="vertical-align: middle"></span>
                        <span class="description-steps-step--label" style="vertical-align: middle">{{ step.title }}
                          <span v-if="step.info_text" data-bs-toggle="tooltip" :title="step.info_text"><span class="mdi mdi-information-outline"></span>
                          </span>
                        </span>
                      </div>
                    </div>
                  </template>
                  <div class="col-12">
                    <div class="small">
                      <span class="mdi mdi-clock-time-five-outline"></span> {{ t('clientSelfRegister.start_footer') }}
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 text-end">
                    <button class="btn btn-primary btn-md" type="button" @click="nextStep">
                      <span>{{ t('clientSelfRegister.start_onboarding') }}</span>
                      <span class="arrow-right-icon"></span>
                    </button>
                  </div>
                </div>
              </template>
              <template v-if="step.type === 'client_form'">
                <Form @submit="processClientForm">
                  <div class="row">
                    <div class="col-12 col-md-6 form-group">
                      <label for="firstnameInput" class="form-label required-label">{{ t('clientSelfRegister.name') }}</label>
                      <Field v-model="step.data.firstname" id="firstnameInput" name="firstnameInput" class="form-control" rules="required" placeholder="Jméno"/>
                      <ErrorMessage name="firstnameInput"/>
                    </div>
                    <div class="col-12 col-md-6 form-group">
                      <label for="surnameInput" class="form-label required-label">{{ t('clientSelfRegister.surname') }}</label>
                      <Field v-model="step.data.surname" id="surnameInput" name="surnameInput" class="form-control" rules="required" placeholder="Příjmení"/>
                      <ErrorMessage name="surnameInput"/>
                    </div>
                    <div class="col-12 col-md-6 form-group">
                      <label for="emailInput" class="form-label required-label">E-mail</label>
                      <Field v-model="step.data.email" id="emailInput" name="emailInput" class="form-control" type="email" rules="required|email" @input="handleContactChange('email')" placeholder="E-mail"/>
                      <ErrorMessage name="emailInput"/>
                      <div class="alert alert-danger mt-3" v-if="errorMessageEmail"> {{ errorMessageEmail }}</div>
                    </div>
                    <div class="col-12 col-md-6 form-group">
                      <label for="phoneInput" class="form-label required-label">{{ t('clientSelfRegister.phone_number') }}</label>
                      <Field v-model="step.data.phone" id="phoneInput" name="phoneInput" class="form-control" type="text" rules="required|phoneNumber" @input="handleContactChange('phone')" placeholder="Telefon"/>
                      <ErrorMessage name="phoneInput"/>
                      <small v-html="t('clientSelfRegister.input_phone_number_in_international_format')"></small>
                      <div class="alert alert-danger mt-3" v-if="errorMessageSMS"> {{ errorMessageSMS }}</div>
                    </div>
                    <div class="col-12 col-md-6 form-group">
                      <label for="passwordInput" class="form-label required-label">{{ t('clientSelfRegister.password') }}</label>
                      <Field v-model="step.data.password" id="passwordInput" name="passwordInput" class="form-control" type="password" rules="required|min:8" placeholder="Heslo"/>
                      <ErrorMessage name="passwordInput"/>
                    </div>
                    <div class="col-12 col-md-6 form-group">
                      <label for="passwordConfirmInput" class="form-label required-label">{{ t('clientSelfRegister.password_confirm') }}</label>
                      <Field v-model="step.data.password_conf" id="passwordConfirmInput" name="passwordConfirmInput" class="form-control" type="password" rules="required|min:8|sameAs:passwordInput" placeholder="Potvrzení hesla"/>
                      <ErrorMessage name="passwordConfirmInput"/>
                    </div>
                    <div class="col-12 col-md-6 form-group">
                      <div class="form-check mt-3 mb-3">
                        <input v-model="showReferralCode" id="showReferralCode" type="checkbox" class="form-check-input"/>
                        <label for="showReferralCode" class="form-check-label">Mám referenční kód</label>
                      </div>
                    </div>
                    <div v-if="showReferralCode" class="col-12 col-md-6 form-group mb-3">
                      <label for="referralCodeInput" class="form-label">{{ t('clientSelfRegister.referral_code') }}</label>
                      <Field v-model="step.data.referral_code" id="referralCodeInput" name="referralCodeInput" class="form-control"/>
                    </div>
                  </div>
                  <div class="alert alert-danger mt-3" v-if="errorMessage"> {{ errorMessage }}</div>
                  <div class="row mb-3">
                    <div class="col-12 d-flex">
                      <input v-model="step.data.gdpr_agree" disabled id="terms_agree" type="checkbox" style="opacity: 1" class="form-check-input form-check-input-bigger me-2" required @change="handleVerificationShow"/>
                      <label style="opacity: 1" for="terms_agree" class="form-check-label required-label">
                        <span> Registrací potvrzujete, že jste se seznámili s dokumentem <a href="https://in-equity.cz//images/inequity/documents/1727682947_INE%20GDPR%20-%20Informace%20o%20ochran%C4%9B%20osobn%C3%ADch%20%C3%BAdaj%C5%AF%2009-2024.pdf" target="_blank">Informace o ochraně osobních údajů</a>.</span>
                      </label>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12 text-end">
                      <button v-if="step && !showVerification" class="btn btn-primary btn-md ms-4" id="register-submit-button" type="submit">
                        <span>Verifikovat</span>
                        <span class="arrow-right-icon"></span>
                      </button>
                    </div>
                  </div>
                </Form>
                <div v-if="showVerification && createdClient" class="mb-5">
                  <form @submit.prevent="completeVerification">
                    <div class="form-group mb-3">
                      <div class="form-label d-flex justify-content-between">
                        <label v-if="!emailIsChanged" class="" for="2fa_code_email"><span>{{ t('clientSelfRegister.email_code_label') }}</span><br/><small class="">{{ t('clientSelfRegister.email_code_sent', {email: step.data.email}) }}</small></label>
                        <label v-else class="" for="2fa_code_email"><span>{{ t('clientSelfRegister.email_code_label') }}</span><br/><small class="">{{ t('clientSelfRegister.email_code_sent_new', {email: step.data.email}) }}</small></label>
                      </div>

                      <input id="2fa_code_email" class="form-control"
                             type="text" placeholder="" autocomplete="off"
                             v-model="step.data.verification_code_email"/>

                      <small class="mt-2">
                        <small v-if="codeSentLoading.EMAIL" class="spinner-border spinner-border-sm" role="status">
                          <span class="visually-hidden">{{ t('dashboardClient.loadingMain') }}</span>
                        </small>
                        <a class="cursor-pointer" @click="generateCode('EMAIL')" v-if="codeRequestInterval['EMAIL'] === 0 && !codeSentLoading['EMAIL']"><small>{{ t('login.send_code') }} </small> <small v-if="codeSent.EMAIL"> {{ t('login.again') }} </small> </a>
                        <small class="text-muted" v-if="codeRequestInterval.EMAIL > 0">{{ t('login.auth_code_sent') }} {{ codeRequestInterval['EMAIL'] }}{{ t('login.secs') }}</small>
                      </small>
                    </div>
                    <div class="form-group">
                      <div class="form-label d-flex justify-content-between">
                        <label v-if="!phoneIsChanged" class="" for="2fa_code_sms"><span>{{ t('clientSelfRegister.sms_code_label') }}</span><br/><small class="">{{ t('clientSelfRegister.sms_code_sent', {phone: maskPhoneNumber(step.data.phone)}) }}</small></label>
                        <label v-else class="" for="2fa_code_sms"><span>{{ t('clientSelfRegister.sms_code_label') }}</span><br/><small class="">{{ t('clientSelfRegister.sms_code_sent_new', {phone: maskPhoneNumber(step.data.phone)}) }}</small></label>
                      </div>

                      <input id="2fa_code_sms" class="form-control"
                             type="text" placeholder="" autocomplete="off"
                             v-model="step.data.verification_code_sms"/>

                      <small class="mt-2">
                        <small v-if="codeSentLoading.SMS" class="spinner-border spinner-border-sm" role="status">
                          <span class="visually-hidden">{{ t('dashboardClient.loadingMain') }}</span>
                        </small>
                        <a class="cursor-pointer" @click="generateCode('SMS')" v-if="codeRequestInterval['SMS'] === 0 && !codeSentLoading['SMS']"><small>{{ t('login.send_code') }} </small> <small v-if="codeSent.SMS"> {{ t('login.again') }} </small> </a>
                        <small class="text-muted" v-if="codeRequestInterval.SMS > 0">{{ t('login.auth_code_sent') }} {{ codeRequestInterval['SMS'] }}{{ t('login.secs') }}</small>
                      </small>
                    </div>
                    <div class="alert alert-danger mt-3" v-if="errorMessage"> {{ errorMessage }}</div>
                    <div class="text-end">
                      <button :disabled="!step.data.verification_code_sms && !step.data.verification_code_email" v-if="step" id="step-submit-button" class="btn btn-primary btn-md">
                        <span>{{ t('clientSelfRegister.continue') }}</span>
                        <span class="arrow-right-icon"></span>
                      </button>
                    </div>
                  </form>

                </div>
              </template>
              <template v-if="step.type === 'password_form'">
                <Form v-on:submit="processClientForm">
                  <div class="row">
                    <div class="col-12 col-md-6 form-group">
                      <label for="passwordInput" class="form-label required-label">{{ t('clientSelfRegister.password') }}</label>
                      <Field v-model="step.data.password" id="passwordInput" name="passwordInput" class="form-control" type="password" rules="required|min:8|sameAs:passwordConfirmInput"/>
                      <ErrorMessage name="passwordInput"/>
                    </div>
                    <div class="col-12 col-md-6 form-group">
                      <label for="passwordConfirmInput" class="form-label required-label">{{ t('clientSelfRegister.password_confirm') }}</label>
                      <Field v-model="step.data.password_conf" id="passwordConfirmInput" name="passwordConfirmInput" class="form-control" type="password" rules="required|min:8|sameAs:passwordInput"/>
                      <ErrorMessage name="passwordConfirmInput"/>
                    </div>
                    <div class="col-12">
                      <div class="alert alert-danger mt-3" v-if="errorMessage"> {{ errorMessage }}</div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12 text-end">
                      <button v-if="step" class="btn btn-primary btn-md ms-4" id="step-submit-button" type="submit">
                        <span>{{ t('clientSelfRegister.continue') }}</span>
                        <span class="arrow-right-icon"></span>
                      </button>
                    </div>
                  </div>
                </Form>
              </template>
              <template v-if="step.type === 'confirmation'">
                <div class="row mb-3 justify-content-center">
                  <div class="col-md-8">
                    <form @submit.prevent="completeVerification">
                      <div class="form-group">
                        <div class="text-center mb-3">{{ t('login.for_login_enter_auth_code') }}</div>
                        <label class="form-label" for="2fa_code">{{ t('login.phone_number2') }}</label>
                        <input id="2fa_code" class="form-control"
                               type="text" placeholder="" autocomplete="off" readonly disabled
                               :value="maskPhoneNumber(phone)" required/>
                      </div>
                      <div class="form-group">
                        <div class="form-label d-flex justify-content-between">
                          <label class="required-label" for="2fa_code">{{ t('login.auth_code') }}</label>
                        </div>
                        <input id="2fa_code" class="form-control"
                               type="text" placeholder="" autocomplete="off"
                               v-model="step.data.verification_code" required/>
                        <div class="mt-2">
                            <span v-if="codeSentLoading" class="spinner-border spinner-border-sm" role="status">
                              <span class="visually-hidden">{{ t('dashboardClient.loadingMain') }}</span>
                            </span>
                          <a class="cursor-pointer" @click="generateCode" v-if="codeRequestInterval === 0 && !codeSentLoading"><span>{{ t('login.send_code') }} </span> <span v-if="codeSent"> {{ t('login.again') }} </span> </a>
                          <span class="text-muted" v-if="codeRequestInterval > 0">{{ t('login.auth_code_sent') }} {{ codeRequestInterval }}{{ t('login.secs') }}</span>
                        </div>
                      </div>
                      <div class="alert alert-danger mt-3" v-if="errorMessage"> {{ errorMessage }}</div>
                      <div class="text-end">
                        <button :disabled="!step.data.verification_code" v-if="step" id="step-submit-button" class="btn btn-primary btn-md">
                          <span>{{ t('clientSelfRegister.continue') }}</span>
                          <span class="arrow-right-icon"></span>
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </template>
              <template v-if="step.type === 'end'">
                <div class="row mb-3">
                  <div class="col-12 text-center">
                    <i class="mdi mdi-check icon-primary icon-big"></i>
                  </div>
                  <h3 class="col-12 mt-5 mb-5 text-center">
                    Děkujeme! Máte vše hotovo.
                  </h3>
                </div>
                <div class="row">
                  <div class="col-12 text-center">
                    <router-link to="/payment-terms" class="btn btn-primary btn-lg" type="button" @click="endOnboarding">Chci vložit prostředky</router-link>
                    <br/>
                    <button class="btn btn-link btn-lg mt-5" type="button" @click="endOnboarding">Jít do aplikace</button>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
    <OnboardingFooter v-if="!isEmbedded"></OnboardingFooter>
  </div>
</template>

<style scoped>
.arrow-right-icon {
  margin-left: 5px;
  line-height: 18px;
  font-size: 18px;
}

.arrow-right-icon::before {
  content: "\f0054";
  font: normal normal normal 18px / 1 "Material Design Icons";
}

h3 {
  margin: 20px 0 10px 0;
}

h5 {
  margin: 14px 0 6px 0;
}

p {
  margin-bottom: 3px;
}

.card {
  border-radius: 15px;

  .card-header {
    background: rgb(249 250 251);
  }
}

.self-register-embedded {
  #app-popup-scrollable-element, .app-popup__container {
    padding: 0 !important;
  }
}

small {
  font-size: 12px !important;
  color: var(--bs-secondary-color) !important;
  font-weight: 400 !important;
}

</style>
